import { call, put, takeLatest } from 'redux-saga/effects';
import { commentService } from './comment.service';
import { commentThreadKey } from './comment.constants';
import {
    CREATE_COMMENT,
    DELETE_COMMENT,
    EDIT_COMMENT,
    GET_COMMENTS,
    GET_COMMENTS_SUCCESS,
    GET_MENTION_STAFF,
    GET_MENTION_STAFF_SUCCESS,
} from './comment.type';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';

/** Normalize the list response into the { data, count } shape the reducer stores. */
const normalizeList = (resData) => ({
    data: Array.isArray(resData) ? resData : resData?.data || [],
    count: Array.isArray(resData) ? resData.length : resData?.count || 0,
});

function* getComments(action) {
    const { onModel, resourceId, params, load } = action.payload;
    if (load !== 'noLoad') {
        yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    }
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    try {
        const res = yield call(commentService.getCommentsService, onModel, resourceId, params);
        const { data, count } = normalizeList(res.data);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put({ type: GET_COMMENTS_SUCCESS, payload: { key: commentThreadKey(onModel, resourceId), data, count } });
    } catch (err) {
        yield put({
            type: GET_COMMENTS_SUCCESS,
            payload: { key: commentThreadKey(onModel, resourceId), data: [], count: 0 },
        });
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        if (err?.data?.message) {
            yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
        }
    }
}

/** After a mutation, refetch the affected thread so the panel reflects the change. */
function* refetchThread(requestParams) {
    if (!requestParams) return;
    const { onModel, resourceId, params } = requestParams;
    const res = yield call(commentService.getCommentsService, onModel, resourceId, params);
    const { data, count } = normalizeList(res.data);
    yield put({ type: GET_COMMENTS_SUCCESS, payload: { key: commentThreadKey(onModel, resourceId), data, count } });
}

function* createComment(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(commentService.createCommentService, action.payload.body);
        yield refetchThread(action.payload.requestParams);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

function* editComment(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(
            commentService.editCommentService,
            action.payload.id,
            action.payload.body,
            action.payload.requestParams?.onModel,
        );
        yield refetchThread(action.payload.requestParams);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

function* deleteComment(action) {
    yield put(httpRequestsOnErrorsActions.removeError(action.type));
    yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(action.type));
    try {
        yield call(commentService.deleteCommentService, action.payload.id, action.payload.requestParams?.onModel);
        yield refetchThread(action.payload.requestParams);
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(action.type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put(httpRequestsOnErrorsActions.appendError(action.type, err?.data?.message));
    }
}

/** Page size for the @mention staff autocomplete (infinite scroll). */
export const MENTION_PAGE_SIZE = 50;

function* getMentionStaff(action) {
    const { search = '', page = 1, load } = action.payload || {};
    if (load !== 'noLoad') {
        yield put(httpRequestsOnLoadActions.appendLoading(action.type));
    }
    try {
        const res = yield call(commentService.getMentionStaffService, {
            search,
            skip: (page - 1) * MENTION_PAGE_SIZE,
            limit: MENTION_PAGE_SIZE,
        });
        // /staff/basic/info may return a bare array or { staffs }/{ staff }/{ data }.
        const raw = Array.isArray(res.data) ? res.data : res.data?.staffs || res.data?.staff || res.data?.data || [];
        const staff = raw.map((s) => ({
            id: s?.id || s?._id,
            firstName: s?.firstName || '',
            lastName: s?.lastName || '',
        }));
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
        yield put({
            type: GET_MENTION_STAFF_SUCCESS,
            payload: { staff, page, hasMore: raw.length >= MENTION_PAGE_SIZE },
        });
    } catch (err) {
        yield put({ type: GET_MENTION_STAFF_SUCCESS, payload: { staff: [], page, hasMore: false } });
        yield put(httpRequestsOnLoadActions.removeLoading(action.type));
    }
}

export const watchComments = function* watchCommentsSaga() {
    yield takeLatest(GET_COMMENTS, getComments);
    yield takeLatest(CREATE_COMMENT, createComment);
    yield takeLatest(EDIT_COMMENT, editComment);
    yield takeLatest(DELETE_COMMENT, deleteComment);
    yield takeLatest(GET_MENTION_STAFF, getMentionStaff);
};
